import { Box, Typography } from "@mui/material";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { memo, useState } from "react";
import { useChartDataSelector } from "../assetAllocation/editPortfolioGroup/useChartDataSelector";
import { CarDownloadButton } from "@app/components/Buttons";
import { openInNewTab } from "@app/utils";
import { assetClassAnalysisPages } from "@app/const/route.const";
import {
  CarAssetClassDftItem,
  useAssetClassSummaryStat,
} from "./useAssetClassSummaryStat";
import { ChartDftTotalReturns } from "@app/components/ChartDftTotalReturns";
import { ChartDftVersusTenYearReturn } from "@app/components/ChartDftVersusTenYearReturn";
import { useChartActiveElement } from "@app/components/ChartTooltip";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import { ChartDftTotalReturnsSummary } from "@app/pages/assetAllocation/editPortfolioGroup/ChartDftTotalReturnsSummary";
import { CarSwitch } from "@app/components/Switch";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { CarAssetClassSelector } from "@app/components/AssetClassSelector";
import { AssetClassLoadType } from "@app/features/assets/useAssetClasses";
import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { ChartDftVersusTenYearReturnBoxAndWhisker } from "@app/components/ChartDftVersusTenYearReturnBoxAndWhisker";
import { CarSelectOptionField } from "@app/components/Inputs";
import {
  DftChartKind,
  useDftChartKindSelector,
} from "./useDftChartKindSelector";

export const AssetClassAnalysisDistanceFromTrend = memo(() => {
  const [isCompare, setIsCompare] = useState(false);

  const assetClassAnalysis = useAssetClassSummaryStat({
    type: AssetClassLoadType.byLatestSimulationJob,
    filterByUsableInPortfolios: true,
  });

  const enabledDftItems = assetClassAnalysis.dftItems.filter(
    (i) => !i.isDisabled,
  );

  const chartDataSelector = useChartDataSelector({
    storageKey: "AssetClassAnalysisDistanceFromTrend_chartDataSelector",
    data1DefaultId: enabledDftItems.at(0)?.level4Id,
    data2DefaultId: enabledDftItems.at(1)?.level4Id,
  });

  const activeElement1 = useChartActiveElement();
  const activeElement2 = useChartActiveElement();

  const dftChartKindSelector = useDftChartKindSelector({
    storageKey: "AssetClassAnalysisDistanceFromTrend_dftChartKindSelector",
  });

  const pageContent = usePageContent();

  const handleShowPdf = () => {
    if (chartDataSelector.data1Id) {
      openInNewTab(
        assetClassAnalysisPages.getPdfDistanceFromTrendReportUrl({
          isCompare,
          assetClass1Id: chartDataSelector.data1Id,
          assetClass2Id: isCompare ? (chartDataSelector.data2Id ?? "") : "",
        }),
      );
    }
  };

  let item1: CarAssetClassDftItem | undefined;
  let item2: CarAssetClassDftItem | undefined;

  if (isCompare) {
    const items = assetClassAnalysis.getDftItemsForCompare(
      chartDataSelector.data1Id,
      chartDataSelector.data2Id,
    );
    item1 = items.item1;
    item2 = items.item2;
  } else {
    item1 = assetClassAnalysis.dftItems.find(
      (i) => i.level4Id === chartDataSelector.data1Id,
    );
    item2 = undefined;
  }

  return (
    <TablePlaceHolder
      isLoading={assetClassAnalysis.isLoading || pageContent.isLoading}
      rows={8}
    >
      <CarDownloadButton
        onClick={handleShowPdf}
        sx={{ mr: -1.5, alignSelf: "flex-end" }}
      >
        Download PDF
      </CarDownloadButton>
      <CarPageContent
        sx={{ mt: 1 }}
        isWhiteContext
        content={pageContent.getContent(
          CarPageContentCode.ASSET_CLASS_MONTHLY_DISTANCE_FROM_TREND_DIRECTIONS,
        )}
      />

      <Box
        sx={{
          mt: 5,
          mb: 3,
          display: "flex",
          alignItems: "flex-end",
          gap: 5,
        }}
      >
        <CarAssetClassSelector
          label={isCompare ? "Asset Class 1" : "Asset Class"}
          sx={{
            width: 250,
            ".MuiInputBase-root": {
              backgroundColor: "white",
            },
          }}
          items={assetClassAnalysis.dftItems}
          valueField="level4Id"
          value={chartDataSelector.data1Id}
          onChange={chartDataSelector.onData1IdChange}
        />
        {isCompare && (
          <CarAssetClassSelector
            label="Asset Class 2"
            sx={{
              width: 250,
              ".MuiInputBase-root": {
                backgroundColor: "white",
              },
            }}
            items={assetClassAnalysis.dftItems}
            valueField="level4Id"
            value={chartDataSelector.data2Id}
            onChange={chartDataSelector.onData2IdChange}
          />
        )}

        <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="par01Regular">Single</Typography>
          <CarSwitch
            size="small"
            checked={isCompare}
            onChange={(e, value) => setIsCompare(value)}
          />
          <Typography variant="par01Regular">Compare</Typography>
          <CarTooltipBox
            sx={{ ml: -0.5 }}
            className={pendoClasses.assetClassAnalysisDftSingleCompare}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor: "white",
          borderStyle: "solid",
          borderWidth: "1px",
          borderColor: "gray3",
          p: 6.5,
        }}
      >
        {item1 && (
          <>
            <ChartDftTotalReturnsSummary dftItem={item1} isWhiteContext />
            <ChartDftTotalReturns
              sx={{ mt: 4 }}
              dftItem={item1}
              pendoPrefix="asset_class_analysis_dft_total_returns"
              activeElement={activeElement1}
              isWhiteContext
            />
            {!isCompare && (
              <>
                <Box
                  sx={{ mt: 5, display: "flex", gap: 2, alignItems: "center" }}
                >
                  <Typography variant="h6SemiBold">
                    Chart Selection -
                  </Typography>
                  <CarSelectOptionField
                    sx={{ width: 180 }}
                    {...dftChartKindSelector}
                  />
                </Box>
                {dftChartKindSelector.value === DftChartKind.indexPoints ? (
                  <ChartDftVersusTenYearReturn
                    sx={{ mt: 4 }}
                    dftItem={item1}
                    pendoPrefix="asset_class_analysis_dft_versus_ten_year_return"
                    activeElement={activeElement1}
                    isWhiteContext
                  />
                ) : (
                  <ChartDftVersusTenYearReturnBoxAndWhisker
                    sx={{ mt: 4 }}
                    dftItem={item1}
                    isWhiteContext
                  />
                )}
              </>
            )}
          </>
        )}
        {isCompare && item2 && (
          <>
            <ChartDftTotalReturnsSummary
              sx={{ mt: 4 }}
              dftItem={item2}
              isWhiteContext
            />
            <ChartDftTotalReturns
              sx={{ mt: 4.5 }}
              dftItem={item2}
              pendoPrefix="asset_class_analysis_dft_total_returns"
              activeElement={activeElement2}
              isWhiteContext
            />
          </>
        )}
      </Box>
    </TablePlaceHolder>
  );
});
